/**
 * RLQ — LobbyEntryExternal
 *
 * Read-only confirmation surface for visitors who landed in the lobby
 * **from outside the branch** — online appointment bookings, branch QR
 * pre-arrival, kiosk pre-flight on a partner site, etc.
 *
 * Audience: Manager+ today (admin-demo surface). When the external entry
 * pipeline is wired up via `/v3/lobby/external` (TBD), the same surface
 * doubles as the operator's "incoming today" view.
 *
 * Today this is a read-only filtered list of `?origin in (kiosk, online,
 * phone)` lobby sessions. No write actions; no claim CTA — staff still
 * claim from the regular Queue.
 */

import { useEffect, useMemo, useState } from 'react';
import { Wifi, RefreshCw } from '@retail-ops/panel-shell/fi';

import { Button, Pill } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { SessionDto } from '@retail-ops/shared-types';

import {
  buildBranchMap,
  buildServiceMap,
  sessionToVisitor,
  sortLobbyVisitors,
  type LobbyVisitor,
} from '../shared/lobbyData';
import { buildMockLobbySessions, isLobbyMockEnabled } from '../shared/lobbyMock';
import { FALLBACK_SERVICE_TYPES } from '../types';
import { LobbyQueueCard } from '../queue/LobbyQueueCard';

const EXTERNAL_ORIGINS = new Set(['kiosk', 'online', 'phone']);

export default function LobbyEntryExternal(): JSX.Element {
  const client = useApiClient();
  const { config } = useConfig();

  const [sessions, setSessions] = useState<ReadonlyArray<SessionDto>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState<number>(0);

  const serviceMap = useMemo(() => buildServiceMap(FALLBACK_SERVICE_TYPES), []);
  const branchMap = useMemo(() => buildBranchMap(config.branches), [config.branches]);

  useEffect(() => {
    if (isLobbyMockEnabled()) {
      setSessions(buildMockLobbySessions());
      setLoading(false);
      setError(null);
      return;
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    client.retail.sessions
      .list({ status: 'active', top: 100 }, { signal: ctrl.signal })
      .then((res) => {
        if (ctrl.signal.aborted) return;
        const filtered = (res.sessions ?? []).filter(
          (s) => s.sessionType === 'lobby' && EXTERNAL_ORIGINS.has(s.origin),
        );
        setSessions(filtered);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setSessions([]);
        setError(err instanceof Error ? err.message : 'Unable to load incoming visitors.');
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [client, tick]);

  const visitors = useMemo<ReadonlyArray<LobbyVisitor>>(() => {
    let walkInIndex = 0;
    const adapted = sessions.map((s) => {
      walkInIndex += 1;
      return sessionToVisitor(s, { serviceMap, branchMap, walkInIndex });
    });
    return sortLobbyVisitors(adapted);
  }, [sessions, serviceMap, branchMap]);

  return (
    <div className="lobby-page">
      <header className="lobby-page__header">
        <div className="lobby-page__title-row">
          <h1 className="lobby-page__title">External arrivals</h1>
          <Pill variant="muted">Read-only</Pill>
          {error && <Pill variant="err">Load error</Pill>}
        </div>
        <p className="lobby-page__subtitle">
          Visitors arriving via online booking, the kiosk, or a phone hand-off
          appear here as they check in. Claim them from the main lobby queue.
        </p>
      </header>

      <div className="lobby-toolbar">
        <Pill variant="info" icon={<Wifi size={11} />}>
          {visitors.length} pending
        </Pill>
        <Button
          variant="ghost"
          size="sm"
          startIcon={<RefreshCw size={12} />}
          onClick={() => setTick((n) => n + 1)}
        >
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="lobby-empty">Loading external arrivals…</div>
      ) : visitors.length === 0 ? (
        <div className="lobby-empty lobby-empty--art">
          <div className="lobby-empty__art" aria-hidden>
            <Wifi size={28} />
          </div>
          <div className="lobby-empty__title">No external arrivals right now</div>
          <div className="lobby-empty__hint">
            Online bookings + kiosk check-ins land here in real time.
          </div>
        </div>
      ) : (
        <div className="rop-card-grid">
          {visitors.map((v) => (
            <LobbyQueueCard
              key={v._sessionId}
              visitor={v}
              canClaim={false}
              claimDisabledReason="Claim from the main lobby queue."
              onClaim={() => {
                /* read-only surface */
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
